import api from './api';

export interface PopulatedTeamMemberUser {
  _id: string;
  name: string;
  email: string;
  avatar?: string;
  phone?: string;
  role: string;
}

export interface TeamMember {
  // The backend populates this path (`members.userId`) whenever a team is
  // fetched with `includeMembers`/by id, so it arrives as the user object,
  // not a bare id string — there is no separate `user` field.
  userId: string | PopulatedTeamMemberUser;
  role: 'lead' | 'member';
  joinedAt: string;
}

export interface Team {
  _id: string;
  name: string;
  description?: string;
  organizationId: string;
  members: TeamMember[];
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  isActive: boolean;
  /** Role modules this team's members are restricted to — unset/empty means unrestricted. */
  allowedModules?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface TeamStats {
  totalTeams: number;
  activeTeams: number;
  totalMembers: number;
  teamsBySize: Array<{ _id: number; count: number }>;
}

export interface TeamsResponse {
  teams: Team[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

export interface CreateTeamData {
  name: string;
  description?: string;
  memberIds?: string[];
}

export interface UpdateTeamData {
  name?: string;
  description?: string;
  isActive?: boolean;
}

export const teamApi = {
  // Get all teams for the organization
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
    includeMembers?: boolean;
  }) => {
    return api.get<{ success: boolean; data: TeamsResponse }>('/teams', { params });
  },

  // Get single team by ID
  getById: (id: string) => {
    return api.get<{ success: boolean; data: Team }>(`/teams/${id}`);
  },

  // Create new team
  create: (data: CreateTeamData) => {
    return api.post<{ success: boolean; data: Team; message: string }>('/teams', data);
  },

  // Update team
  update: (id: string, data: UpdateTeamData) => {
    return api.put<{ success: boolean; data: Team; message: string }>(`/teams/${id}`, data);
  },

  // Delete team
  delete: (id: string) => {
    return api.delete<{ success: boolean; message: string }>(`/teams/${id}`);
  },

  // Add member to team
  addMember: (teamId: string, userId: string, role: 'lead' | 'member' = 'member') => {
    return api.post<{ success: boolean; data: Team; message: string }>(`/teams/${teamId}/members`, {
      userId,
      role
    });
  },

  // Remove member from team
  removeMember: (teamId: string, userId: string) => {
    return api.delete<{ success: boolean; data: Team; message: string }>(`/teams/${teamId}/members/${userId}`);
  },

  // Update member role
  updateMemberRole: (teamId: string, userId: string, role: 'lead' | 'member') => {
    return api.put<{ success: boolean; data: Team; message: string }>(`/teams/${teamId}/members/${userId}`, {
      role
    });
  },

  // Get available users for team membership
  getAvailableUsers: (teamId: string) => {
    return api.get<{ success: boolean; data: Array<{
      _id: string;
      name: string;
      email: string;
      avatar?: string;
      role: string;
    }> }>(`/teams/${teamId}/available-users`);
  },

  // Get team statistics
  getStats: () => {
    return api.get<{ success: boolean; data: TeamStats }>('/teams/stats/overview');
  },

  // The caller's own effective module restriction across all their team
  // memberships — null means unrestricted. See backend's teamCapabilityService.ts.
  getMyCapabilities: () => {
    return api.get<{ success: boolean; data: { allowedModules: string[] | null } }>('/teams/my-capabilities');
  },

  // Org Owner/Admin restricts which Role modules this team's members may use at all.
  setAllowedModules: (teamId: string, allowedModules: string[]) => {
    return api.put<{ success: boolean; data: { allowedModules: string[] } }>(`/teams/${teamId}/allowed-modules`, { allowedModules });
  },

  // Get teams for current user
  getMyTeams: () => {
    return api.get<{ success: boolean; data: Team[] }>('/teams/my-teams');
  }
};
